import {
  type KeyboardEventHandler,
  type MouseEventHandler,
  useMemo,
} from 'react';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing';
import { consumeEvent } from '@frameio/utils';
import { Text, theme } from '@frameio/vapor';
import { IconTrash } from '@frameio/vapor-icons';
import { useIsActiveItem, useOnItemSelect } from '../contexts';
import type { ProjectTreeItem, TrashTreeItemProps } from '../types';
import TEXT from './generated/TEXT/Trash';
import { ICON_WIDTH, ItemContainer } from './TreeItem';

export function Trash({ id, type }: TrashTreeItemProps) {
  const item = useMemo<ProjectTreeItem>(
    () => ({
      id,
      type,
    }),
    [id, type]
  );
  const projectId = useActiveProjectId();
  const onItemSelect = useOnItemSelect();
  const isActive = useIsActiveItem(item);

  const href = getLink({
    page: 'trash',
    projectId,
  });

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (event.button === 2 || event.metaKey) return;

    onItemSelect(item, href);
  };

  const captureKeyEvents = ['Enter', 'ArrowRight', 'ArrowLeft'];

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === 'Enter') {
      onItemSelect(item, href);
    }

    if (captureKeyEvents.includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <div onContextMenu={consumeEvent}>
      <ItemContainer
        expandable={false}
        id={id}
        isActive={isActive}
        isDisabled={false}
        isLink={true}
        isOpen={true}
        name={TEXT.recentlyDeleted}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        paddingLeft={PADDING_LEFT}
        type={type}
      >
        <IconTrash
          color={theme.colors.icon.primary}
          size={ICON_WIDTH}
          style={{ flexShrink: 0 }}
        />
        <Text
          color={isActive ? 'primary' : undefined}
          truncate={true}
          variant="body"
        >
          {TEXT.recentlyDeleted}
        </Text>
      </ItemContainer>
    </div>
  );
}

const PADDING_LEFT = 16;

TEXT.DEFINE({
  /** Name of the "Trash" folder containing recently deleted assets */
  recentlyDeleted: 'Recently Deleted',
});
